

import {
    OpenInNew as ExternalLink,
    LocationOn as MapPin,
    Delete as Trash2,
    People as Users
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { Character, Event, Location } from '@/lib/types';

interface EventDetailsModalProps {
  open: boolean;
  onClose: () => void;
  event: Event | null;
  characters: Character[];
  locations: Location[];
  onDetach: (eventId: string) => void;
}

export function EventDetailsModal({
  open,
  onClose,
  event,
  characters,
  locations,
  onDetach,
}: EventDetailsModalProps) {
  const { t } = useTranslation();

  if (!event) return null;

  const involvedCharacters = characters.filter((c) => event.characterIds?.includes(c.id));
  const involvedLocations = locations.filter((l) =>
    (event.locationIds || []).includes(l.id)
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {event.name}
        <Button
          component={Link}
          to={`/events/edit/${event.id}`}
          size="small"
          startIcon={<ExternalLink sx={{ fontSize: 16 }} />}
        >
          {t('arcs.edit_full_event')}
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Location */}
          {involvedLocations.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <MapPin sx={{ fontSize: 14 }} /> {t('arcs.location')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {involvedLocations.map((loc) => (
                  <Chip key={loc.id} label={loc.name} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                ))}
              </Box>
            </Box>
          )}

          {/* Description */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('arcs.overview')}
            </Typography>
            <Typography variant="body2">
              {event.shortDescription || t('arcs.no_description')}
            </Typography>
          </Box>

          {/* Involved Characters */}
          {involvedCharacters.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Users sx={{ fontSize: 14 }} /> {t('arcs.involved_characters')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {involvedCharacters.map((char) => (
                  <Chip key={char.id} label={char.name} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                ))}
              </Box>
            </Box>
          )}

          {/* Why It Matters */}
          {event.whyItMatters && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {t('arcs.why_it_matters')}
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {event.whyItMatters}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={() => onDetach(event.id)}
          color="error"
          startIcon={<Trash2 sx={{ fontSize: 18 }} />}
          sx={{ fontWeight: 600 }}
        >
          {t('arcs.remove_from_arc')}
        </Button>
        <Button onClick={onClose} variant="outlined">
          {t('arcs.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
